import { Button } from "@components/ui/button"
import { useDrawer } from "@contexts/DrawerContext"
import Case from "@pages/case"
import { AccountEmailForm } from "@components/AccountNew/AccountEmailForm/AccountEmailForm"
import { AccountContactPreferencesForm } from "@components/AccountNew/AccountContactPreferencesForm/AccountContactPreferencesForm"
import type { IAccountNotification } from "@apis/accounts/types"
import { PendingCancellationForm } from "@components/MemberNew/PendingCancellationForm"
import { AddressInfoForm } from "@components/Address/AddressInfoForm"
import { useQueryClient } from "@tanstack/react-query"
import { accountKeys } from "@apis/accounts/queryKeys"
import { useAccount } from "@apis/accounts/useAccounts"

type TAccountNotificationButtonProps = {
  account_id: string
  prospect_guid: string
  notification: IAccountNotification
}

export const AccountNotificationButton = ({
  account_id,
  prospect_guid,
  notification,
}: TAccountNotificationButtonProps) => {
  const { openDrawer, closeDrawer } = useDrawer()
  const queryClient = useQueryClient()
  const getAccountQuery = useAccount(account_id)

  if (!notification.template) return null

  const badEmailTypes = [
    "bad_format_email",
    "blocklisted_email",
    "bounced_email",
  ]

  const handleClick = () => {
    switch (notification.template) {
      case "Case":
        if (notification.params?.case_guid) {
          openDrawer(
            <Case caseGuid={notification.params.case_guid} />,
            "Case Details",
          )
        }
        break
      case "Email":
        if (badEmailTypes.includes(notification.template_type as string)) {
          openDrawer(
            <AccountEmailForm account_id={account_id} />,
            "Update Profile Email",
          )
          return
        }

        if (notification.template_type === "opted_out_email") {
          openDrawer(
            <AccountContactPreferencesForm
              prospect_guid={prospect_guid}
              visibleFields={["can_email"]}
            />,
            "Update Email Contact Preference",
          )
          return
        }
        break
      case "PendingCancellation":
        openDrawer(
          <PendingCancellationForm
            account_id={account_id}
            params={notification.params}
            onSave={() => {
              queryClient.invalidateQueries({
                queryKey: accountKeys.accountNotifications(
                  prospect_guid,
                  account_id,
                ),
              })
              closeDrawer()
            }}
          />,
          "Pending Cancellation",
        )
        break
      case "Address":
        openDrawer(
          <AddressInfoForm
            identifier={account_id}
            address_type_code="billing"
            address_guid={getAccountQuery.data?.mailing_address_address_guid}
            onSave={() => {
              queryClient.invalidateQueries({
                queryKey: accountKeys.detail(account_id),
              })
              closeDrawer()
            }}
            onCancel={() => closeDrawer()}
            showMap
          />,
          "Update Profile Mailing Address",
        )
        break
      default: // Handle other templates if/when needed. In the meantime will return null
        return null
    }
  }

  const buttonVariantMap: Record<string, "default" | "destructive"> = {
    Case: "default",
    Address: "default",
    Email: "destructive",
    PendingCancellation: "default",
  }

  const variant = buttonVariantMap[notification.template] ?? "secondary"

  return (
    <Button
      variant={variant}
      className="tw:mt-2"
      size="sm"
      onClick={handleClick}
    >
      View
    </Button>
  )
}
